import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Device } from "@capacitor/device";
import {
  doc,
  setDoc,
  serverTimestamp,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase.config";

const isNative = () => Capacitor.isNativePlatform();
let listenersInitialized = false;

async function getNotificationPreferences(userId) {
  try {
    const localKey = `notif-prefs-${userId}`;
    const stored = localStorage.getItem(localKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (_) {}
    }
    const ref = doc(db, "user-settings", userId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return (
        data?.notifications || { quizCompletions: true, friendRequests: true }
      );
    }
  } catch (_) {}
  return { quizCompletions: true, friendRequests: true };
}

function inferNotificationCategory(event) {
  const tag = event?.notification?.data?.category || event?.data?.category;
  if (tag === "quiz") return "quiz";
  if (tag === "friend") return "friend";

  // Heuristics based on title/body
  const title = (event?.notification?.title || "").toLowerCase();
  const body = (event?.notification?.body || "").toLowerCase();
  if (title.includes("quiz") || body.includes("quiz")) return "quiz";
  if (title.includes("friend") || body.includes("friend")) return "friend";
  return "general";
}

function isCategoryAllowedByPrefs(category, prefs) {
  if (category === "quiz") return !!prefs.quizCompletions;
  if (category === "friend") return !!prefs.friendRequests;
  return true; // general notifications allowed by default
}

async function ensurePermissions() {
  const permStatus = await PushNotifications.checkPermissions();
  if (permStatus.receive === "granted") return true;

  const req = await PushNotifications.requestPermissions();
  return req.receive === "granted";
}

async function registerPushLayer() {
  // Registers with APNs on iOS; no-op on web
  try {
    await PushNotifications.register();
  } catch (e) {
    // swallow; registration might already be done
  }
}

async function getFcmToken() {
  const { token } = await FirebaseMessaging.getToken();
  return token;
}

async function getFcmTokenWithRetry(maxAttempts = 5, delayMs = 600) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const token = await getFcmToken();
      if (token) return token;
    } catch (_) {}
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}

async function registerAndSetupListeners(user) {
  if (!user || user.isAnonymous) return null;
  // Registers APNs (iOS) or no-op as needed
  await registerPushLayer();

  // Debug registration listeners (add once)
  if (!listenersInitialized) {
    try {
      PushNotifications.addListener("registration", (t) => {
        console.log("[push] APNs registered, token length:", t?.value?.length);
      });
      PushNotifications.addListener("registrationError", (err) => {
        console.error("[push] APNs registration error:", err);
      });
    } catch (_) {}
  }

  // Ensure FCM auto init is enabled
  try {
    await FirebaseMessaging.setAutoInitEnabled({ enabled: true });
  } catch (_) {}

  const initialToken = await getFcmTokenWithRetry();
  if (initialToken) {
    await upsertTokenForUser(user.uid, initialToken);
  } else {
    console.warn("[push] FCM token unavailable after retries");
  }

  if (!listenersInitialized) {
    const currentUserId = user.uid;
    // Foreground message handling with user preferences
    FirebaseMessaging.addListener("notificationReceived", async (event) => {
      try {
        const category = inferNotificationCategory(event);
        const prefs = await getNotificationPreferences(currentUserId);
        const allowed = isCategoryAllowedByPrefs(category, prefs);
        if (!allowed) return;

        // Preserve payload for tap handling via LocalNotifications extra
        const payload = {
          ...(event?.notification?.data || {}),
          ...(event?.data || {}),
          category,
        };

        await LocalNotifications.schedule({
          notifications: [
            {
              id: Date.now() % 2147483647,
              title: event?.notification?.title || "Notification",
              body: event?.notification?.body || "",
              schedule: { at: new Date(Date.now() + 10) },
              extra: payload,
            },
          ],
        });
      } catch (_) {}
    });

    // Minimal tap handler: only route friend request notifications
    const handleNotificationAction = async (data) => {
      try {
        const type = data?.type || data?.notificationType;
        if (type === "friend_request_received") {
          window.dispatchEvent(
            new CustomEvent("vc-navigate", { detail: { path: "/friends" } })
          );
          return;
        }

        if (type === "vibe_check_completed") {
          const sharedId = data?.sharedId || data?.shared_id || data?.id;
          if (sharedId) {
            try {
              // Load shared quiz to determine quiz route
              const sharedRef = doc(db, "shared-quizzes", sharedId);
              const sharedSnap = await getDoc(sharedRef);
              const quizName = sharedSnap?.exists()
                ? sharedSnap.data()?.quizName
                : null;
              const quizRoutes = {
                "best-date": "/best-date/results",
                "political-alignment": "/political-alignment/results",
                "sex-styles": "/sex-styles/results",
                "personality-check": "/personality-check/results",
                "attachment-styles": "/attachment-styles/results",
              };
              const basePath = quizRoutes[quizName];
              if (basePath) {
                window.dispatchEvent(
                  new CustomEvent("vc-navigate", {
                    detail: { path: `${basePath}?shared=${sharedId}` },
                  })
                );
                return;
              }
            } catch (_) {}
          }
        }

        if (type === "friend_request_accepted") {
          const friendId =
            data?.friendId || data?.accepterId || data?.uid || data?.userId;
          if (friendId) {
            try {
              const profileRef = doc(db, "public-profiles", friendId);
              const snap = await getDoc(profileRef);
              const usernameRaw = snap?.exists() ? snap.data()?.username : null;
              const username =
                typeof usernameRaw === "string"
                  ? usernameRaw.toLowerCase()
                  : null;
              const path = username ? `/${username}` : "/friends";
              window.dispatchEvent(
                new CustomEvent("vc-navigate", { detail: { path } })
              );
              return;
            } catch (_) {
              window.dispatchEvent(
                new CustomEvent("vc-navigate", { detail: { path: "/friends" } })
              );
              return;
            }
          }
        }
      } catch (_) {}
    };

    // Remote push tap (background/terminated -> opened)
    try {
      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action) => {
          const data = action?.notification?.data || {};
          handleNotificationAction(data);
        }
      );
    } catch (_) {}

    // Local notification tap (foreground-generated notification)
    try {
      LocalNotifications.addListener(
        "localNotificationActionPerformed",
        (action) => {
          const data = action?.notification?.extra || {};
          handleNotificationAction(data);
        }
      );
    } catch (_) {}

    // Token refresh handling
    FirebaseMessaging.addListener("tokenReceived", async (e) => {
      try {
        if (e?.token) {
          await upsertTokenForUser(currentUserId, e.token);
        }
      } catch (_) {}
    });

    listenersInitialized = true;
  }

  return initialToken;
}

export async function checkPermissionStatus() {
  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive; // 'granted' | 'denied' | 'prompt'
  } catch (e) {
    return "denied";
  }
}

export async function initializeIfPermitted(user) {
  try {
    if (!user || user.isAnonymous) return;
    if (!isNative()) return;

    const status = await PushNotifications.checkPermissions();
    if (status.receive !== "granted") return;

    // Local notifications permission (will auto-grant if already allowed)
    try {
      await LocalNotifications.requestPermissions();
    } catch (_) {}

    await registerAndSetupListeners(user);
  } catch (error) {
    console.error("Failed to initialize (granted) push notifications:", error);
  }
}

export async function requestAndRegister(user) {
  try {
    if (!user || user.isAnonymous) return false;
    if (!isNative()) return false;

    const req = await PushNotifications.requestPermissions();
    const granted = req.receive === "granted";
    if (!granted) return false;

    try {
      await LocalNotifications.requestPermissions();
    } catch (_) {}
    await registerAndSetupListeners(user);
    return true;
  } catch (error) {
    console.error("Failed to request/register push notifications:", error);
    return false;
  }
}

async function upsertTokenForUser(userId, token) {
  if (!userId || !token) return;
  const deviceInfo = await Device.getId();
  const platform = Capacitor.getPlatform();
  const ref = doc(db, "users", userId, "pushTokens", token);
  await setDoc(
    ref,
    {
      token,
      platform,
      deviceId: deviceInfo.identifier,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

async function deleteTokenForUser(userId, token) {
  if (!userId || !token) return;
  const ref = doc(db, "users", userId, "pushTokens", token);
  await deleteDoc(ref);
}

export async function initializePushNotifications(user) {
  try {
    if (!user || user.isAnonymous) return;
    if (!isNative()) return;

    const granted = await ensurePermissions();
    if (!granted) return;

    try {
      await LocalNotifications.requestPermissions();
    } catch (_) {}
    await registerAndSetupListeners(user);
  } catch (error) {
    console.error("Failed to initialize push notifications:", error);
  }
}

export async function cleanupPushNotifications(user) {
  try {
    if (!user) return;
    if (!isNative()) return;
    // Best-effort: delete current token mapping
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (token) await deleteTokenForUser(user.uid, token);
    } catch (_) {}
  } catch (error) {
    console.warn("Cleanup push notifications failed:", error);
  }
}

const PushService = {
  initializePushNotifications,
  cleanupPushNotifications,
  checkPermissionStatus,
  initializeIfPermitted,
  requestAndRegister,
};

export default PushService;
